import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { DIRECTUS_INSTANCE, fetchCurrentUser } from '../../../api';
import { Alert } from '../../../ui/atoms';
import { translateRole } from '../../../constants/roles';
import { formatDisplayDateTime } from '../../../utils/dateFormatting';

const WorkspaceTab = ({ formData }) => {
  const { t, i18n } = useTranslation();
  const [workspaceData, setWorkspaceData] = useState(null);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWorkspaceData = async () => {
      const token = localStorage.getItem('directus_token');
      if (!token) {
        setError(t('userProfile.workspace.errors.noAuthToken'));
        setLoading(false);
        return;
      }

      try {
        // Get the current user first to get workspace ID
        const userData = await fetchCurrentUser(['linked_workspace.id']);
        const workspaceId = userData?.linked_workspace?.id;
        
        if (!workspaceId) {
          setError(t('userProfile.workspace.errors.noWorkspace'));
          setLoading(false);
          return;
        }

        // Get detailed workspace data
        const workspaceResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/workspace/${workspaceId}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            fields: ['*']
          }
        });
        
        setWorkspaceData(workspaceResponse.data.data);

        // Get users associated with this workspace
        const usersResponse = await axios.get(`${DIRECTUS_INSTANCE}/users`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: {
              linked_workspace: { id: { _eq: workspaceId } }
            },
            fields: ['id', 'email', 'first_name', 'last_name', 'role', 'status', 'last_access']
          }
        });
        
        setWorkspaceUsers(usersResponse.data.data || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching workspace data:', error);
        setError(t('userProfile.workspace.errors.loadFailed'));
        setLoading(false);
      }
    };

    fetchWorkspaceData();
  }, [t]);

  const formatDate = (dateString) => {
    if (!dateString) return t('common.noData');
    return formatDisplayDateTime(dateString, i18n.language);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (error) {
    return <Alert variant="error" message={error} />;
  }

  return (
    <div className="space-y-12">
      <div className="pb-12">
        <h2 className="text-3xl font-medium leading-7 text-gray-900">{t('userProfile.workspace.title')}</h2>
        <p className="mt-4 text-sm leading-6 text-gray-600">
          {t('userProfile.workspace.description')}
        </p>

        {workspaceData && (
          <div className="mt-10 overflow-hidden bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">{t('userProfile.workspace.detailsTitle')}</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">ID: {workspaceData.id}</p>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">{t('userProfile.workspace.fields.name')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{workspaceData.workspace_name}</dd>
                </div>


                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">{t('common.createdAt')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{formatDate(workspaceData.date_created)}</dd>
                </div>

                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">{t('profile.language')}</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{workspaceData.language || t('common.noData')}</dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        <div className="mt-10">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">{t('userProfile.workspace.usersTitle')}</h3>
          
          {workspaceUsers.length === 0 ? (
            <p className="text-sm text-gray-500">{t('userProfile.workspace.noUsers')}</p>
          ) : (
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">{t('common.name')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('common.email')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('userProfile.workspace.fields.role')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('common.status')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('userProfile.workspace.fields.lastAccess')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {workspaceUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {user.first_name} {user.last_name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{user.email}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{translateRole(user.role)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.last_access ? formatDate(user.last_access) : t('userProfile.workspace.never')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceTab; 